import { MouseEventButton } from "@cervval.js/client-html5-core/src/services/mouse.types";
import { ValueOrFunction, anyReallyAny } from "@cervval.js/common/src/tools/object.tools";

/** Uid of available drag and drop events */
export type DragAndDropEventUid = "dragstart" | "dragover" | "dragend" | "drop" | "dragabort";

/** Direction of the drag, either 'up', 'down' or null (if mouse is moving horizontally and not vertically). */
export type DragAndDropVerticalDirection = "up" | "down" | null;

/** Direction of the drag, either 'left', 'right' or null (if mouse is moving vertically and not horizontally). */
export type DragAndDropHorizontalDirection = "left" | "right" | null;

/** Point of drag and drop */
export type DragAndDropPoint = { x: number, y: number };

/**
 * Direction of pointer from drag start point
 */
export type PointerDirection = -1 | 0 | 1;

/** Keeps track of the direction in which the user is dragging along each axis. */
export type PointerDirectionDelta = { x: PointerDirection, y: PointerDirection };

/** Config of drag ref */
export type DragRefConfig<ItemData = anyReallyAny> = Partial<{
    /** if disabled, drag and drop on element will have no effect */
    disabled: ValueOrFunction<boolean>,

    /** ZIndex of dragPreview */
    zIndex: number,

    /** Class to be added to the preview element. */
    previewClass?: string | string[],

    /**
     * Placeholder element that will follow the cursor.
     * 
     * If this function is defined, the original element will be hidden during the drag and drop
     * and the placeholder will follow the cursor.
     * 
     * If the function returns "global", the original element will be cloned and placed in the
     * placeholder container.
     */
    placeholder: (data: ItemData) => HTMLElement | "global",

    /**
     * Function to create a container for the placeholders.
     * By default, create the placeholder in a new div on document.body
     * 
     * The placeholder returned by this function will be deleted after the drop.
     */
    placeholderContainer: (data: ItemData) => HTMLElement,

    /**
     * Allowed directions of drag and drop move 
     * Use it to allow moving elements in only one single direction
     * If undefined, direction allowed is XY
     * 
     * Does not prevent drop event from being emitted if the element is dragged out of the allowed directions.
     */
    allowedDirections?: "x" | "y",

    /**
     * Only emit the events if the given mouse buttons triggered the mouseup event.
     * By default trigger a drag and drop on any mouse button.
     */
    mouseButtonsAllowed?: Set<MouseEventButton>
}>

//#region css variables

/** If attribute is defined, element is a cjs drop container */
export const CJS_DROP_CONTAINER_ATTR = "data-cjs-drop-container";

/** If attribute is defined, element is a cjs drop item */
export const CJS_DRAGGABLE_ITEM_ATTR = "data-cjs-draggable-item";